/**
 * Composant - IdentityVerificationStep
 *
 * Étape 2: Vérification de la pièce d'identité
 * Gère les comptes joints : affiche les signataires validés et permet
 * d'en ajouter d'autres avant de passer au formulaire de retrait.
 *
 * INTÉGRATION CBS:
 * - Appelle POST /api/v1/cbs-integration/clients/accounts pour récupérer les comptes
 * - Appelle POST /api/v1/documents-identites/check-or-save pour sauvegarder le document
 */

import { useState, useEffect } from 'react';
import { IdCard, ArrowRight, CheckCircle, UserPlus, Users, Fingerprint, CreditCard, AlertCircle, AlertTriangle, RefreshCw, Download } from 'lucide-react';
import { WithdrawalState } from '../../../domain/aggregates/WithdrawalWorkflow';
import { IdentityDocumentType } from '../../../domain/entities/Identity';
import { toast } from 'sonner';
import './etapes.css';
import { useAuth } from '../../contexts/AuthContext';
import { CBSClientAccountGateway, ParsedAccount } from '../../../infrastructure/adapters/CBSClientAccountGateway';
import { IdentityDocumentGateway } from '../../../infrastructure/adapters/IdentityDocumentGateway';

// Instances des gateways
const cbsGateway = new CBSClientAccountGateway();
const identityGateway = new IdentityDocumentGateway();

interface IdentityVerificationStepProps {
  workflow: any;
}

export function IdentityVerificationStep({ workflow }: IdentityVerificationStepProps) {
  const { state, context, verifyIdentity, proceedToForm, addAnotherClient, loading } = workflow;
  const { user } = useAuth();

  // Récupérer le code de l'utilisateur connecté
  const userCode = user?.cardData?.code || user?.username || '';

  // États du formulaire
  const [idType, setIdType] = useState<string>('CNI');
  const [idNumber, setIdNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [issuePlace, setIssuePlace] = useState('');

  // États CBS
  const [cbsAccounts, setCbsAccounts] = useState<ParsedAccount[]>([]);
  const [cbsCards, setCbsCards] = useState<string[]>([]);
  const [cbsLoading, setCbsLoading] = useState(false);
  const [cbsError, setCbsError] = useState<string | null>(null);
  const [cbsLoaded, setCbsLoaded] = useState(false);

  // États PID CBS (pré-remplissage du formulaire)
  const [pidLoading, setPidLoading] = useState(false);
  const [pidLoaded, setPidLoaded] = useState(false);

  // États du modal de confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [pendingVerification, setPendingVerification] = useState<any>(null);
  /** nouvelle : pièce inconnue enregistrée · rattacher : pièce connue mais sans compte */
  const [motifModal, setMotifModal] = useState<'nouvelle' | 'rattacher'>('nouvelle');

  // Récupérer les infos depuis le contexte
  const account = context?.account;
  const authenticatedClients = context?.authenticatedClients ?? [];
  const lastBiometricResult = context?.lastBiometricResult;

  // Infos client depuis le résultat biométrique stocké dans le contexte
  const clientName = lastBiometricResult?.clientName ?? 'Client inconnu';
  const cardNumber = lastBiometricResult?.clientId ?? null;
  const biometricAuthId = lastBiometricResult?.authId ?? null;

  // Support compte joint (si account est disponible)
  const accountHolder = account?.primaryHolder ?? null;
  const isJoint = account?.isJoint?.() ?? false;
  const allHolders = account?.holders ?? [];
  const coHolders = account?.getCoHolders?.() ?? [];

  // Nombre total de signataires requis pour un compte joint
  const requiredSignatures = isJoint ? allHolders.length : 1;
  const currentSignatures = authenticatedClients.length;
  const allSigned = currentSignatures >= requiredSignatures;

  // Identifier les co-titulaires qui n'ont pas encore signé
  const unsignedCoHolders = coHolders.filter(
    (h: any) => !authenticatedClients.some((c: any) => c.clientId === h.id)
  );

  /**
   * Récupérer le numéro de carte depuis le résultat biométrique
   * Le clientId contient le numéro de carte (PAN) lu depuis getCardData champ 8
   */
  const getCardNumber = (): string | null => {
    return cardNumber;
  };

  /**
   * Charger les comptes CBS au montage du composant
   */
  useEffect(() => {
    if (state === WithdrawalState.IDENTITY_VERIFICATION && !cbsLoaded) {
      loadCBSAccounts();
    }
  }, [state]);

  /**
   * Appelle l'API CBS pour récupérer les comptes et cartes
   */
  const loadCBSAccounts = async () => {
    const cardNumber = getCardNumber();

    if (!cardNumber) {
      setCbsError('Numéro de carte non disponible');
      return;
    }

    if (!userCode) {
      setCbsError('Code agent non disponible - veuillez vous reconnecter');
      console.error('[IdentityVerification] Code utilisateur non disponible depuis AuthContext');
      return;
    }

    setCbsLoading(true);
    setCbsError(null);

    try {
      console.log('[IdentityVerification] Récupération comptes CBS pour carte:', cardNumber);
      console.log('[IdentityVerification] Code agent utilisé:', userCode);

      const result = await cbsGateway.getClientAccounts(cardNumber, userCode);

      setCbsAccounts(result.accounts);
      setCbsCards(result.cards);
      setCbsLoaded(true);

      console.log('[IdentityVerification] Comptes chargés:', result.accounts.length);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
      setCbsError(errorMsg);
      console.error('[IdentityVerification] Erreur chargement CBS:', error);
    } finally {
      setCbsLoading(false);
    }
  };

  /**
   * Récupère la pièce d'identité du client depuis le CBS et pré-remplit le formulaire.
   * L'agent peut ensuite valider visuellement et soumettre (qui appelle checkOrSaveDocument).
   */
  const handleLoadPidFromCBS = async () => {
    const cn = getCardNumber();
    if (!cn) {
      toast.error('Numéro de carte non disponible');
      return;
    }

    setPidLoading(true);
    try {
      console.log('[IdentityVerification] Récupération PID CBS pour carte:', cn);
      const { pid } = await cbsGateway.getClientPid(cn);

      if (!pid.numero) {
        toast.warning('Aucune pièce d\'identité trouvée dans le CBS pour cette carte');
        return;
      }

      // Pré-remplir le formulaire
      setIdType(pid.type);
      setIdNumber(pid.numero);
      setIssueDate(pid.dateDelivrance);
      setExpiryDate(pid.dateExpiration);
      if (pid.paysEmission) setIssuePlace(pid.paysEmission);
      setPidLoaded(true);

      if (pid.dateExpiration && pid.dateExpiration < dateDuJour()) {
        toast.warning(`La pièce enregistrée au CBS (${pid.numero}) est expirée depuis le ${formaterDate(pid.dateExpiration)}`);
      } else {
        toast.success(`Pièce d'identité récupérée : ${pid.numero}`);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
      toast.error(`Erreur CBS PID : ${errorMsg}`);
      console.error('[IdentityVerification] Erreur PID CBS:', error);
    } finally {
      setPidLoading(false);
    }
  };

  /**
   * Confirmer l'enregistrement de la nouvelle pièce avec le numéro de compte sélectionné
   */
  const handleConfirmNewDocument = async () => {
    if (!pendingVerification || !selectedAccount) {
      toast.error('Veuillez sélectionner un compte');
      return;
    }

    try {
      // Mettre à jour le document avec le numéro de compte sélectionné
      console.log('[IdentityVerification] Mise à jour du document avec compte:', selectedAccount);

      const updateResult = await identityGateway.checkOrSaveDocument({
        typeDocument: idType,
        numeroDocument: idNumber,
        dateExpiration: expiryDate,
        dateDelivrance: issueDate,
        adresse: issuePlace,
        numeroCompte: selectedAccount,
        carteId: 0,
        numeroCarte: cardNumber ?? '',
        codeUser: userCode,
      });

      // Message selon le statut
      if (updateResult?.status === 'UPDATED') {
        toast.success(`Document mis à jour avec le compte ${selectedAccount}`);
      } else {
        toast.success(`Pièce enregistrée pour le compte ${selectedAccount}`);
      }

      console.log('[IdentityVerification] Résultat mise à jour:', updateResult);

      // Fermer le modal
      setShowConfirmModal(false);

      // Vérifier l'identité dans le workflow
      const success = await verifyIdentity(
        pendingVerification.currentClientId,
        pendingVerification.currentClientName,
        pendingVerification.lastBioAuthId,
        pendingVerification.identityData
      );

      if (success) {
        toast.success('Identité vérifiée — passage à l\'étape suivante');
      }

      // Réinitialiser
      setPendingVerification(null);
      setSelectedAccount('');

    } catch (error) {
      console.error('[IdentityVerification] Erreur mise à jour document:', error);
      toast.error(error instanceof Error ? error.message : "L'opération n'a pas pu être réalisée.");
    }
  };

  /**
   * Annuler l'enregistrement de la nouvelle pièce
   */
  const handleCancelNewDocument = () => {
    setShowConfirmModal(false);
    setPendingVerification(null);
    setSelectedAccount('');
    toast.info('Enregistrement annulé');
  };

  /**
   * Soumission du formulaire avec intégration CBS
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const erreurPiece = controlerDatesPiece(issueDate, expiryDate);
    if (erreurPiece) {
      toast.error(erreurPiece.titre);
      return;
    }

    if (!lastBiometricResult || !cardNumber) {
      toast.error('Aucune authentification biométrique détectée');
      return;
    }

    // Utiliser les infos de l'auth biométrique (clientName, clientId = numCarte)
    const currentClientName = clientName;
    const currentClientId = cardNumber;

    // Récupérer le biometricAuthId depuis le résultat biométrique
    const lastBioAuthId = biometricAuthId ?? 'BIO-' + Date.now();

    try {
      // 1. Sauvegarder/vérifier le document d'identité dans le backend SANS numéro de compte
      console.log('[IdentityVerification] Vérification existence du document d\'identité...');

      const docResult = await identityGateway.checkOrSaveDocument({
        typeDocument: idType,
        numeroDocument: idNumber,
        dateExpiration: expiryDate,
        dateDelivrance: issueDate,
        adresse: issuePlace,
        carteId: 0,
        numeroCarte: currentClientId,
        codeUser: userCode,
      });

      // Feedback selon le statut retourné
      const docStatus = docResult?.status;

      if (docStatus === 'EXISTING') {
        // Pièce existante : continuer normalement
        toast.success('Pièce conforme à la base de données');
        console.log('[IdentityVerification] Document existant, statut:', docStatus);

        // Vérifier l'identité dans le workflow
        const success = await verifyIdentity(
          currentClientId,
          currentClientName,
          lastBioAuthId,
          {
            type: idType as IdentityDocumentType,
            number: idNumber,
            issueDate: new Date(issueDate),
            expiryDate: new Date(expiryDate),
            issueCountry: '',
            issuePlace: issuePlace,
            holderName: currentClientName,
          }
        );

        if (success) {
          toast.success('Identité vérifiée — passage à l\'étape suivante');
        }

      } else if (docStatus === 'CREATED' || docStatus === 'A_RATTACHER') {
        // Pièce à rattacher à un compte du client : fenêtre de choix du compte
        setMotifModal(docStatus === 'CREATED' ? 'nouvelle' : 'rattacher');

        // Stocker les données pour la vérification après confirmation
        setPendingVerification({
          currentClientId,
          currentClientName,
          lastBioAuthId,
          identityData: {
            type: idType as IdentityDocumentType,
            number: idNumber,
            issueDate: new Date(issueDate),
            expiryDate: new Date(expiryDate),
            issueCountry: '',
            issuePlace: issuePlace,
            holderName: currentClientName,
          },
          docResult,
        });

        // Pré-sélectionner le premier compte disponible
        if (cbsAccounts.length > 0) {
          setSelectedAccount(cbsAccounts[0].accountNumber);
        } else if (account?.accountNumber?.value) {
          setSelectedAccount(account.accountNumber.value);
        }

        setShowConfirmModal(true);

      } else if (docStatus === 'UPDATED') {
        toast.success('Pièce rattachée au compte');

        // Vérifier l'identité dans le workflow
        const success = await verifyIdentity(
          currentClientId,
          currentClientName,
          lastBioAuthId,
          {
            type: idType as IdentityDocumentType,
            number: idNumber,
            issueDate: new Date(issueDate),
            expiryDate: new Date(expiryDate),
            issueCountry: '',
            issuePlace: issuePlace,
            holderName: currentClientName,
          }
        );

        if (success) {
          toast.success('Identité vérifiée — passage à l\'étape suivante');
        }
      } else {
        toast.error("Réponse inattendue lors de la vérification de la pièce : réessayez.");
      }

    } catch (error) {
      console.error('[IdentityVerification] Erreur lors de la soumission:', error);
      toast.error(error instanceof Error ? error.message : "L'opération n'a pas pu être réalisée.");
    }
  };

  if (!lastBiometricResult || !lastBiometricResult.success) {
    return (
      <div className="et">
        <div className="et-note erreur">
          <AlertCircle />
          <p>Aucune authentification biométrique valide : recommencez le retrait depuis l&apos;étape Biométrie.</p>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // ÉTAT: IDENTITY_VERIFIED — Récapitulatif des signataires
  // ════════════════════════════════════════════════════════════
  if (state === WithdrawalState.IDENTITY_VERIFIED) {
    return (
      <div className="et">
        <section className="et-carte">
          <div className="et-corps">
            <div className="et-resultat">
              <span className="rond">
                <CheckCircle />
              </span>
              <h3>Identité vérifiée</h3>
              <p>
                {currentSignatures} signataire{currentSignatures > 1 ? 's' : ''} vérifié{currentSignatures > 1 ? 's' : ''}
                {isJoint && ` sur ${requiredSignatures} requis`}
              </p>
            </div>

            <div>
              <p className="et-section-titre">
                <span>
                  <Users /> Signataires vérifiés
                </span>
              </p>
              <div className="et-liste">
                {authenticatedClients.map((client: any, index: number) => (
                  <div key={client.clientId} className="et-ligne ok">
                    <span className="et-num">{index + 1}</span>
                    <div className="txt">
                      <p className="titre">{client.clientName}</p>
                      <p className="detail">
                        {libellePiece(client.identity?.type)} n° {client.identity?.number}
                      </p>
                    </div>
                    <CheckCircle />
                  </div>
                ))}
              </div>
            </div>

            {/* ─── Compte joint : co-titulaires restants ─── */}
            {isJoint && !allSigned && unsignedCoHolders.length > 0 && (
              <div>
                <p className="et-section-titre">
                  <span>
                    <Fingerprint /> En attente de signature
                  </span>
                </p>
                <div className="et-liste">
                  {unsignedCoHolders.map((holder: any) => (
                    <div key={holder.id} className="et-ligne attente">
                      <span className="et-num">?</span>
                      <div className="txt">
                        <p className="titre">{holder.name}</p>
                        <p className="detail">Doit insérer sa carte et poser son doigt sur le capteur</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isJoint && !allSigned && unsignedCoHolders.length > 0 ? (
              <button type="button" onClick={() => addAnotherClient()} className="et-btn ambre">
                <UserPlus />
                Authentifier le signataire suivant
              </button>
            ) : (
              (!isJoint || allSigned) && (
                <button type="button" onClick={() => proceedToForm()} className="et-btn principal">
                  Continuer vers le montant
                  <ArrowRight />
                </button>
              )
            )}
            {isJoint && allSigned && <p className="et-aide">Tous les co-titulaires ont été vérifiés.</p>}
          </div>
        </section>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // ÉTAT: IDENTITY_VERIFICATION — Formulaire de saisie
  // ════════════════════════════════════════════════════════════
  const currentHolderIndex = authenticatedClients.length;
  const currentHolderName = accountHolder
    ? (currentHolderIndex === 0 ? accountHolder.name : (allHolders[currentHolderIndex]?.name ?? clientName))
    : clientName;
  const aujourdhui = dateDuJour();
  const erreurPiece = controlerDatesPiece(issueDate, expiryDate);
  const pieceExpiree = erreurPiece?.champ === 'expiration';

  return (
    <div className="et">
      <section className="et-carte">
        <header className="et-entete">
          <span className="ico">
            <IdCard />
          </span>
          <div>
            <h2>Pièce d&apos;identité</h2>
            <p>Contrôlez la pièce présentée par le client.</p>
          </div>
          {isJoint && (
            <span className="droite et-pastille gris">
              Signataire {currentHolderIndex + 1}/{requiredSignatures}
            </span>
          )}
        </header>

        <div className="et-corps">
          {/* Client authentifié */}
          <div className="et-client">
            <span className="et-avatar">{initiales(currentHolderName)}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p className="nom">{currentHolderName}</p>
              <p className="sous">
                Carte <span className="et-mono">{masquerCarte(cardNumber)}</span>
                {isJoint && ' · Compte joint'}
              </p>
            </div>
            <span className="et-pastille vert">
              <Fingerprint /> Empreinte vérifiée
            </span>
          </div>

          {isJoint && authenticatedClients.length > 0 && (
            <div className="et-note info">
              <CheckCircle />
              <p>
                Déjà vérifié{authenticatedClients.length > 1 ? 's' : ''} :{' '}
                {authenticatedClients.map((c: any) => c.clientName).join(', ')}
              </p>
            </div>
          )}

          {/* Comptes liés à la carte (CBS) */}
          <div>
            <p className="et-section-titre">
              <span>
                <CreditCard /> Comptes liés à la carte
              </span>
              {!cbsLoading && (
                <button type="button" onClick={loadCBSAccounts} className="et-btn lien">
                  <RefreshCw /> Actualiser
                </button>
              )}
            </p>

            {cbsLoading && (
              <div className="et-charge">
                <RefreshCw className="et-tourne" /> Chargement des comptes…
              </div>
            )}

            {cbsError && !cbsLoading && (
              <div className="et-note erreur">
                <AlertCircle />
                <p>{cbsError}</p>
              </div>
            )}

            {!cbsLoading && !cbsError && cbsAccounts.length > 0 && (
              <div className="et-liste">
                {cbsAccounts.map((acc, idx) => (
                  <div key={idx} className="et-ligne">
                    <div className="txt">
                      <p className="titre et-mono">{acc.accountNumber}</p>
                      <p className="detail">
                        {[acc.holderName, acc.type, acc.currency, acc.branchName].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <span className={`et-pastille ${acc.status === 'A' ? 'vert' : 'ambre'}`}>
                      {acc.status === 'A' ? 'Actif' : acc.status}
                    </span>
                  </div>
                ))}
                <p className="et-aide" style={{ textAlign: 'left' }}>
                  {cbsAccounts.length} compte{cbsAccounts.length > 1 ? 's' : ''}
                  {cbsCards.length > 0 && ` · ${cbsCards.length} carte${cbsCards.length > 1 ? 's' : ''}`}
                </p>
              </div>
            )}

            {!cbsLoading && !cbsError && cbsLoaded && cbsAccounts.length === 0 && (
              <p className="et-vide">Aucun compte trouvé pour cette carte.</p>
            )}
            {!cbsLoading && !cbsError && !cbsLoaded && <p className="et-vide">Comptes non chargés.</p>}
          </div>

          {/* Pièce d'identité */}
          <form onSubmit={handleSubmit}>
            <p className="et-section-titre">
              <span>
                <IdCard /> Pièce présentée
              </span>
              <button
                type="button"
                onClick={handleLoadPidFromCBS}
                disabled={pidLoading || !cardNumber}
                className="et-btn lien"
                title="Pré-remplir avec la pièce enregistrée au CBS"
              >
                {pidLoading ? <RefreshCw className="et-tourne" /> : pidLoaded ? <CheckCircle /> : <Download />}
                {pidLoading ? 'Récupération…' : pidLoaded ? 'Recharger depuis le CBS' : 'Pré-remplir depuis le CBS'}
              </button>
            </p>

            {pidLoaded && (
              <div className="et-note info" style={{ marginBottom: 14 }}>
                <CheckCircle />
                <p>Données pré-remplies depuis le CBS : comparez-les avec la pièce physique avant de valider.</p>
              </div>
            )}

            <div className="et-grille">
              <div className="et-champ">
                <label htmlFor="id-type">
                  Type de pièce <span className="req">*</span>
                </label>
                <select id="id-type" className="et-input" value={idType} onChange={(e) => setIdType(e.target.value)} required>
                  <option value="CNI">Carte nationale d&apos;identité</option>
                  <option value="PASSPORT">Passeport</option>
                  <option value="DRIVER_LICENSE">Permis de conduire</option>
                  <option value="CONSULAR_CARD">Carte consulaire</option>
                </select>
              </div>
              <div className="et-champ">
                <label htmlFor="id-numero">
                  Numéro <span className="req">*</span>
                </label>
                <input
                  id="id-numero"
                  className="et-input"
                  type="text"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder="Ex. CI123456"
                  autoComplete="off"
                  required
                />
              </div>
              <div className="et-champ">
                <label htmlFor="id-emission">
                  Date d&apos;émission <span className="req">*</span>
                </label>
                <input
                  id="id-emission"
                  className="et-input"
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  max={aujourdhui}
                  required
                />
              </div>
              <div className="et-champ">
                <label htmlFor="id-expiration">
                  Date d&apos;expiration <span className="req">*</span>
                </label>
                <input
                  id="id-expiration"
                  className={`et-input ${pieceExpiree ? 'erreur' : ''}`}
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  aria-invalid={pieceExpiree}
                  aria-describedby={pieceExpiree ? 'erreur-piece' : undefined}
                  required
                />
                {pieceExpiree && <span className="et-erreur-champ">Expirée {depuisQuand(expiryDate)}</span>}
              </div>
              <div className="et-champ pleine">
                <label htmlFor="id-adresse">
                  Adresse <span className="opt">(facultatif)</span>
                </label>
                <input
                  id="id-adresse"
                  className="et-input"
                  type="text"
                  value={issuePlace}
                  onChange={(e) => setIssuePlace(e.target.value)}
                  placeholder="Ex. Abidjan, Côte d'Ivoire"
                />
              </div>
            </div>

            {erreurPiece && (
              <div className="et-note erreur" id="erreur-piece" role="alert" style={{ marginTop: 16 }}>
                <AlertTriangle />
                <div>
                  <strong>{erreurPiece.titre}</strong>
                  {erreurPiece.texte}
                </div>
              </div>
            )}

            <button type="submit" disabled={loading || !!erreurPiece} className="et-btn principal" style={{ marginTop: 20 }}>
              {loading ? (
                <>
                  <RefreshCw className="et-tourne" /> Vérification…
                </>
              ) : (
                <>
                  Valider l&apos;identité
                  <ArrowRight />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="et-pied">
          <AlertTriangle />
          Une pièce expirée ne peut pas être acceptée.
        </p>
      </section>

      {/* Modal de confirmation pour nouvelle pièce */}
      {showConfirmModal && (
        <div className="et-voile" role="dialog" aria-modal="true" aria-labelledby="titre-nouvelle-piece">
          <div className="et-modale">
            <header className="et-entete">
              <span className="ico rouge">
                <AlertTriangle />
              </span>
              <div>
                <h2 id="titre-nouvelle-piece">
                  {motifModal === 'nouvelle' ? 'Pièce différente de celle du compte' : 'Pièce à rattacher au compte'}
                </h2>
                <p>Confirmation requise avant de continuer</p>
              </div>
            </header>

            <div className="et-corps">
              <div className="et-note erreur">
                <AlertTriangle />
                <p>
                  {motifModal === 'nouvelle'
                    ? "La pièce présentée n'est pas celle enregistrée à l'ouverture du compte. Vérifiez-la attentivement : elle sera rattachée au compte choisi."
                    : "Cette pièce est connue mais n'est rattachée à aucun compte. Vérifiez-la, puis choisissez le compte du client."}
                </p>
              </div>

              <div className="et-champ">
                <label htmlFor="compte-piece">
                  Compte associé <span className="req">*</span>
                </label>
                {cbsAccounts.length > 0 ? (
                  <select
                    id="compte-piece"
                    className="et-input"
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    required
                  >
                    <option value="">Sélectionnez un compte</option>
                    {cbsAccounts.map((acc) => (
                      <option key={acc.accountNumber} value={acc.accountNumber}>
                        {acc.accountNumber} — {acc.holderName} ({acc.type})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="et-note alerte">
                    <AlertCircle />
                    <p>Les comptes de la carte ne sont pas chargés. Annulez, cliquez sur « Actualiser » dans « Comptes liés à la carte », puis validez à nouveau la pièce.</p>
                  </div>
                )}
              </div>

              <dl className="et-tableau">
                <div>
                  <dt>Type</dt>
                  <dd>{libellePiece(idType)}</dd>
                </div>
                <div>
                  <dt>Numéro</dt>
                  <dd>{idNumber}</dd>
                </div>
                <div>
                  <dt>Client</dt>
                  <dd>{clientName}</dd>
                </div>
              </dl>

              <div className="et-btns">
                <button type="button" onClick={handleCancelNewDocument} className="et-btn secondaire">
                  Annuler
                </button>
                <button type="button" onClick={handleConfirmNewDocument} disabled={!selectedAccount} className="et-btn principal large">
                  <CheckCircle />
                  Confirmer et continuer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const LIBELLES_PIECES: Record<string, string> = {
  CNI: "Carte nationale d'identité",
  PASSPORT: 'Passeport',
  DRIVER_LICENSE: 'Permis de conduire',
  CONSULAR_CARD: 'Carte consulaire',
};

function libellePiece(type?: string) {
  return (type && LIBELLES_PIECES[type]) || type || 'Pièce';
}

/** Numéro de carte affiché masqué : •••• 2345 */
function masquerCarte(numero?: string | null) {
  if (!numero) return '—';
  return numero.length > 4 ? `•••• ${numero.slice(-4)}` : numero;
}

function initiales(nom?: string) {
  return (nom ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((m) => m[0]?.toUpperCase())
    .join('') || '?';
}

/** Date du jour au format AAAA-MM-JJ, en heure locale (et non UTC) */
function dateDuJour() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** AAAA-MM-JJ → JJ/MM/AAAA */
function formaterDate(iso: string) {
  const [a, m, j] = iso.split('-');
  return a && m && j ? `${j}/${m}/${a}` : iso;
}

/** « depuis le 11/03/2025 (19 mois) » */
function depuisQuand(iso: string) {
  const debut = new Date(`${iso}T00:00:00`);
  const maintenant = new Date();
  const mois = (maintenant.getFullYear() - debut.getFullYear()) * 12 + (maintenant.getMonth() - debut.getMonth());
  const duree = mois >= 1 ? ` (${mois} mois)` : '';
  return `depuis le ${formaterDate(iso)}${duree}`;
}

/** Règles de validité d'une pièce : non expirée, et émise avant son expiration. */
function controlerDatesPiece(emission: string, expiration: string): { champ: 'expiration' | 'emission'; titre: string; texte: string } | null {
  if (expiration && expiration < dateDuJour()) {
    return {
      champ: 'expiration',
      titre: 'Pièce expirée : identité non acceptable',
      texte: `Cette pièce est expirée ${depuisQuand(expiration)}. Demandez au client une autre pièce en cours de validité (passeport, permis…) et saisissez-la, ou invitez-le à faire mettre à jour sa pièce auprès de l'agence avant tout retrait.`,
    };
  }
  if (emission && expiration && expiration <= emission) {
    return {
      champ: 'emission',
      titre: 'Dates incohérentes',
      texte: "La date d'expiration doit être postérieure à la date d'émission. Vérifiez la saisie.",
    };
  }
  return null;
}
